import { Card, CardContent, Grid, IconButton, Snackbar, SnackbarContent } from '@material-ui/core';
import { green } from "@material-ui/core/colors";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { EmptyState, FilesDropzone, LoadingIndicator, RenderGuard } from 'components';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routine } from "services";
import { ModelsActions } from "store/actions";
import useErrorCatcher from "utils/useErrorCatcher";
import { FileCard } from './components';

const useStyles = makeStyles(theme => ({
  root: {},
  files: {
    marginTop: theme.spacing(3)
  },
  close: {
    padding: theme.spacing(0.5),
  },
  snackbar: {
    backgroundColor: green[400]
  },
  snackbarMessage: {
    display: "flex",
    alignItems: "center",
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  icon: {
    fontSize: 20,
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: "flex-end"
  },
}));

const Files = props => {
  const { className, ...rest } = props;
  const model = useSelector(state => state.models.routine);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setError(error && error.message));
  const [snackOpen, setSnackOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const classes = useStyles();

  const handleUpload = () => {
    if (!files.length) return;
    setLoading(true);
    runAsyncBlock(async () => {
      let response = await Routine.create_visual(model.id, files[0]);
      dispatch(ModelsActions.update_model({ routine: { ...model, visuals: [...model.visuals, response] } }));
      setFiles([]);
      setSnackOpen(true);
      setSnackbarMessage("Upload Success!");
    }).finally(() => setLoading(false));
  }

  const handleDelete = (asset_id) => {
    return () => {
      runAsyncBlock(async () => {
        await Routine.del_visual(model.id, asset_id);
        let visuals = model.visuals;
        let index = model.visuals.findIndex(v => v.id === asset_id);
        visuals.splice(index, 1);
        dispatch(ModelsActions.update_model({ routine: { ...model, visuals } }));
        setSnackOpen(true);
        setSnackbarMessage("Visual Deleted!");
      });
    }
  }

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
  };

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <LoadingIndicator active={loading} variant="card" />
      <RenderGuard renderIf={!loading}>
        <Card>
          <CardContent>
            <FilesDropzone
              files={files}
              error={error}
              setFiles={setFiles}
              setError={setError}
              handleUpload={handleUpload}
              message="Supported formats: jpg/png/mp4/3gp/mkv"
              multiple={true}
              fileLoading={loading} />
          </CardContent>
        </Card>
        <Grid
          className={classes.files}
          container
          spacing={3}
        >
          {model.visuals.map(file => (
            <Grid
              item
              key={file.id}
              lg={4}
              md={4}
              sm={6}
              xs={12}
            >
              <FileCard file={file} handleDelete={handleDelete} />
            </Grid>
          ))}
        </Grid>
        <EmptyState message="There are no visuals uploaded for this routine" active={!model.visuals.length} />
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={snackOpen}
          autoHideDuration={3000}
          onClose={handleSnackClose}
        >
          <SnackbarContent
            className={classes.snackbar}
            message={<span className={classes.snackbarMessage}><CheckCircleIcon className={clsx(classes.icon, classes.iconVariant)} />{snackbarMessage}</span>}
            action={[
              <IconButton
                key="close"
                aria-label="close"
                color="inherit"
                className={classes.close}
                onClick={handleSnackClose}
              >
                <CloseIcon />
              </IconButton>,
            ]}
          />
        </Snackbar>
      </RenderGuard>
    </div>
  );
};

Files.propTypes = {
  className: PropTypes.string,
};

export default Files;
